import React, { useState, useEffect, useRef } from 'react';
import { getSponsors, adminCreateSponsor, adminUpdateSponsor, adminDeleteSponsor, adminGetSponsorInquiries, adminDeleteSponsorInquiry, adminUploadSponsorLogo } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Handshake, Mail, Upload, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', website: '', description: '', tier: 'bronze', type: 'sponsor' });
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);

  const fetchData = async () => {
    try {
      const [sRes, iRes] = await Promise.all([getSponsors(), adminGetSponsorInquiries()]);
      setSponsors(sRes.data);
      setInquiries(iRes.data);
    } catch (error) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDeleteInquiry = async (inq) => {
    if (!window.confirm(`Anfrage von "${inq.company_name}" löschen?`)) return;
    try {
      await adminDeleteSponsorInquiry(inq.inquiry_id);
      toast.success('Anfrage gelöscht');
      fetchData();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await adminUpdateSponsor(editing.sponsor_id, form);
        toast.success('Aktualisiert');
      } else {
        await adminCreateSponsor(form);
        toast.success('Erstellt');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', website: '', description: '', tier: 'bronze', type: 'sponsor' });
      fetchData();
    } catch (error) { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`"${s.name}" wirklich löschen?`)) return;
    try {
      await adminDeleteSponsor(s.sponsor_id);
      toast.success('Gelöscht');
      fetchData();
    } catch (error) { toast.error('Fehler'); }
  };

  const handleLogoUpload = async (sponsorId, file) => {
    if (!file) return;
    setUploading(sponsorId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await adminUploadSponsorLogo(sponsorId, formData);
      toast.success('Logo hochgeladen');
      fetchData();
    } catch (error) {
      toast.error('Logo-Upload fehlgeschlagen');
    } finally {
      setUploading(null);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'platinum': return 'bg-purple-500/20 text-purple-400';
      case 'gold': return 'bg-yellow-500/20 text-yellow-400';
      case 'silver': return 'bg-gray-400/20 text-gray-300';
      default: return 'bg-amber-700/20 text-amber-600';
    }
  };

  const sponsorsList = sponsors.filter(s => (s.type || 'sponsor') === 'sponsor');
  const partnersList = sponsors.filter(s => s.type === 'partner');

  const renderSponsorCard = (s) => (
    <Card key={s.sponsor_id} className="card-surface" data-testid={`sponsor-card-${s.sponsor_id}`}>
      <CardContent className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {s.logo_url ? (
            <img
              src={`${API}/api/files/${s.logo_url}`}
              alt={s.name}
              className="w-20 h-20 object-contain rounded-sm bg-white/5 p-2"
            />
          ) : (
            <div className="w-20 h-20 bg-primary/10 rounded-sm flex items-center justify-center">
              <Handshake className="w-10 h-10 text-primary" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold uppercase">{s.name}</h3>
              <Badge className={getTierColor(s.tier)}>{s.tier}</Badge>
              <Badge variant="outline" className="text-xs">{s.type === 'partner' ? 'Partner' : 'Sponsor'}</Badge>
            </div>
            <p className="text-text-secondary text-sm">{s.description || s.website || 'Keine Beschreibung'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            className="text-accent"
            disabled={uploading === s.sponsor_id}
            onClick={() => {
              fileRef.current?.setAttribute('data-sponsor-id', s.sponsor_id);
              fileRef.current?.click();
            }}
            data-testid={`upload-logo-${s.sponsor_id}`}
          >
            {uploading === s.sponsor_id ? (
              <div className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setEditing(s);
              setForm({
                name: s.name,
                website: s.website || '',
                description: s.description || '',
                tier: s.tier,
                type: s.type || 'sponsor',
              });
              setDialogOpen(true);
            }}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDelete(s)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-sponsors-page">
      <input
        type="file"
        ref={fileRef}
        className="hidden"
        accept="image/*"
        onChange={(e) => {
          const sponsorId = fileRef.current?.getAttribute('data-sponsor-id');
          if (sponsorId && e.target.files[0]) {
            handleLogoUpload(sponsorId, e.target.files[0]);
          }
          e.target.value = '';
        }}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Sponsoren & Partner</h1>
        <Button
          onClick={() => {
            setEditing(null);
            setForm({ name: '', website: '', description: '', tier: 'bronze', type: 'sponsor' });
            setDialogOpen(true);
          }}
          className="btn-primary"
          data-testid="add-sponsor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />Hinzufügen
        </Button>
      </div>

      <Tabs defaultValue="sponsors" className="space-y-6">
        <TabsList className="bg-surface-elevated">
          <TabsTrigger value="sponsors">Sponsoren ({sponsorsList.length})</TabsTrigger>
          <TabsTrigger value="partners">Partner ({partnersList.length})</TabsTrigger>
          <TabsTrigger value="inquiries">Anfragen ({inquiries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="sponsors">
          {sponsorsList.length > 0 ? (
            <div className="space-y-4">{sponsorsList.map(renderSponsorCard)}</div>
          ) : (
            <Card className="card-surface">
              <CardContent className="p-8 text-center">
                <Handshake className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">Keine Sponsoren vorhanden.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="partners">
          {partnersList.length > 0 ? (
            <div className="space-y-4">{partnersList.map(renderSponsorCard)}</div>
          ) : (
            <Card className="card-surface">
              <CardContent className="p-8 text-center">
                <Handshake className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">Keine Partner vorhanden.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="inquiries">
          {inquiries.length > 0 ? (
            <div className="space-y-4">
              {inquiries.map(inq => (
                <Card key={inq.inquiry_id} className="card-surface">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold">{inq.company_name}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-secondary">{formatDate(inq.created_at)}</span>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeleteInquiry(inq)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary mb-2">{inq.contact_name} &middot; {inq.email}</p>
                    <p className="text-sm">{inq.message}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-surface">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">Keine Anfragen vorhanden.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader>
            <DialogTitle>{editing ? 'Bearbeiten' : 'Neuer Sponsor / Partner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  <SelectItem value="sponsor">Sponsor</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input className="input-dark" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="sponsor-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input className="input-dark" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Input className="input-dark" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Stufe</Label>
              <Select value={form.tier} onValueChange={v => setForm({ ...form, tier: v })}>
                <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silber</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} className="btn-primary w-full" data-testid="save-sponsor-btn">
              {editing ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSponsors;
